import { useState } from 'react';
import {
    BlankModal,
    ButtonGroup,
    HStack,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Button,
    IconButton,
    InlineBanner,
    Text,
} from '@cad-arc';
import { LoadingIndicator } from '../common/components/loading-indicator';
import { UnitStateData } from '@mark43/cad-api';
import { useFullscreenModalControls } from '../modals/context/modal-context-provider';
import { useEventPanelViewQuery } from '../event-panel/event-state/use-event-panel-view-query';
import { useUnitAssignment } from '../unit-assignment/unit-assignment-context';
import { useUnitAssignSelfToEventMutation } from '../unit-assignment/use-unit-assign-self-to-event-mutation';
import { useEnRouteStates } from '../common/hooks/use-en-route-states';
import { AssignmentModalDetails } from './components/assignment-modal-details';
import { ASSIGNMENT_MODAL_COMMON_SIZE } from './constants';
import { useFrState } from '../fr-state/use-fr-state';
import { DataPrivacy } from '../common/components/datadog-privacy';

function HeldEventModalBody(props: { heldEventId: number; errorMessage?: string }) {
    const { errorMessage } = props;
    const eventQuery = useEventPanelViewQuery({ eventId: props.heldEventId });
    return (
        <ModalBody>
            {!!errorMessage && <InlineBanner title={errorMessage} status="error" />}
            {!eventQuery.data ? (
                <LoadingIndicator />
            ) : (
                // Unmask non-PII fields for Datadog Session Replay — held prompt only (RND-53990).
                <AssignmentModalDetails eventData={eventQuery.data} unmaskNonPiiFields />
            )}
        </ModalBody>
    );
}

export type SelectHeldEventModalComponentType = typeof SelectHeldEventModal;

export function SelectHeldEventModal(props: { initialHeldEventId: number }): React.ReactElement {
    const unitAssignment = useUnitAssignment();
    const unitId = unitAssignment?.unitId;
    const heldEventIds = unitAssignment?.heldEvents.map((heldEvent) => heldEvent.eventId) ?? [];
    const [selectedHeldEventIndex, setSelectedHeldEventIndex] = useState<number>(
        heldEventIds?.findIndex((id) => id === props.initialHeldEventId) ?? 0
    );
    const selectedHeldEventId = heldEventIds[selectedHeldEventIndex];
    const modalControls = useFullscreenModalControls();
    const unitAssignSelfToEventMutation = useUnitAssignSelfToEventMutation();
    const { enRouteState, alternateEnRouteState } = useEnRouteStates();

    const frState = useFrState();

    const headerText =
        heldEventIds.length > 1
            ? `Held Assignment ${selectedHeldEventIndex + 1}/${heldEventIds.length}`
            : 'Held Assignment';

    const errorMessage = ((unitAssignSelfToEventMutation.error as unknown) as any)?.response?.data
        ?.error;

    const assignEventToUnitState = (unitStateData: UnitStateData) => {
        if (unitId) {
            unitAssignSelfToEventMutation
                // @ts-expect-error types for the endpoint are wrong
                .mutateAsync({
                    agencyEventId: selectedHeldEventId,
                    unitStateId: unitStateData.id,
                    unitId,
                })
                .then((transitioned) => {
                    if (transitioned) {
                        frState.setWasHeldEventsModalShown(true);
                        modalControls.close();
                    }
                });
        }
    };

    const shouldAutoFocusEnRoute = !!enRouteState;
    const shouldAutoFocusAlternateEnRoute = !shouldAutoFocusEnRoute && !!alternateEnRouteState;

    return (
        <BlankModal
            isOpen={true}
            onClose={() => modalControls.close()}
            size="lg"
            style={{ maxHeight: 535 }}
            scrollBehavior="inside"
            closeOnOverlayClick={false}
            closeOnEsc={false}
            hideCloseButton
        >
            <ModalHeader>
                <HStack justifyContent={heldEventIds.length > 1 ? 'space-between' : 'flex-start'}>
                    {heldEventIds.length > 1 ? (
                        <IconButton
                            icon="ArrowLeft"
                            onClick={() => {
                                setSelectedHeldEventIndex((curr) => {
                                    if (curr === 0) {
                                        return heldEventIds.length - 1;
                                    }
                                    return curr - 1;
                                });
                            }}
                        />
                    ) : undefined}
                    <DataPrivacy privacy="unmask">
                        <Text>{headerText}</Text>
                    </DataPrivacy>
                    {heldEventIds.length > 1 ? (
                        <IconButton
                            icon="ArrowRight"
                            onClick={() => {
                                setSelectedHeldEventIndex((curr) => {
                                    if (curr === heldEventIds.length - 1) {
                                        return 0;
                                    }
                                    return curr + 1;
                                });
                            }}
                        />
                    ) : undefined}
                </HStack>
            </ModalHeader>
            <HeldEventModalBody heldEventId={selectedHeldEventId} errorMessage={errorMessage} />
            <ModalFooter>
                {/* Unmask the static action-button labels (Not Now / Code 3 / En Route) — not PII. */}
                <DataPrivacy privacy="unmask">
                    <ButtonGroup>
                        <Button
                            size={ASSIGNMENT_MODAL_COMMON_SIZE}
                            variant="ghost"
                            onClick={() => {
                                frState.setWasHeldEventsModalShown(true);
                                modalControls.close();
                            }}
                        >
                            Not Now
                        </Button>
                        {!!alternateEnRouteState && (
                            <Button
                                size={ASSIGNMENT_MODAL_COMMON_SIZE}
                                variant="outline"
                                isLoading={unitAssignSelfToEventMutation.isLoading}
                                onClick={() => assignEventToUnitState(alternateEnRouteState)}
                                autoFocus={shouldAutoFocusAlternateEnRoute}
                            >
                                {alternateEnRouteState.value}
                            </Button>
                        )}
                        {!!enRouteState && (
                            <Button
                                size={ASSIGNMENT_MODAL_COMMON_SIZE}
                                variant="solid"
                                isLoading={unitAssignSelfToEventMutation.isLoading}
                                onClick={() => assignEventToUnitState(enRouteState)}
                                autoFocus={shouldAutoFocusEnRoute}
                            >
                                {enRouteState.value}
                            </Button>
                        )}
                    </ButtonGroup>
                </DataPrivacy>
            </ModalFooter>
        </BlankModal>
    );
}
